import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Center,
  Flex,
  FormLabel,
  IconButton,
  Input,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast
} from '@chakra-ui/react'
import React, { useEffect, useRef, useState } from 'react'

import { formatDateTime } from '../../../../helpers'
import { ICourseInterface } from '../../../../interfaces/Website/Courses/CoursesCategory/ICoursesCategory'
import { CoursesObjectivesService } from '../../../../services/CoursesObjectives/CoursesObjectivesService'
import { CoursesObjectivesModal } from './CoursesObjectiveModal'

const CoursesObjectivesPage = () => {
  const service = new CoursesObjectivesService()
  const cancelRef = useRef(null)
  const toast = useToast()

  const [objectives, setObjectives] = useState<ICourseInterface[]>([])
  const [filtered, setFiltered] = useState<ICourseInterface[]>([])
  const [error, setError] = useState(false)

  const [search, setSearch] = useState('')

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingObjective, setEditingObjective] = useState<{ id: number; name: string } | null>(null)

  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [idDeleting, setIdDeleting] = useState<number | null>(null)

  const fetchObjectives = async () => {
    try {
      const data = await service.list()
      setObjectives(data)
      setError(false)
    } catch {
      setError(true)
    }
  }

  useEffect(() => {
    setFiltered(objectives.filter((objective) => objective.name.toLowerCase().includes(search.toLowerCase())))
  }, [search, objectives])

  useEffect(() => {
    fetchObjectives()
  }, [])

  function openEditModal(objective: { id: number; name: string }) {
    setEditingObjective(objective)
    setIsModalOpen(true)
  }

  function openCreateModal() {
    setEditingObjective(null)
    setIsModalOpen(true)
  }

  function closeModal() {
    setIsModalOpen(false)
    setEditingObjective(null)
  }

  function openDeleteDialog(id: number) {
    setIdDeleting(id)
    setIsDeleteOpen(true)
  }

  function closeDeleteDialog() {
    setIsDeleteOpen(false)
    setIdDeleting(null)
  }

  async function handleDelete() {
    if (!idDeleting) return

    try {
      await service.delete(idDeleting)
      toast({
        title: 'Objetivo excluído com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true
      })
      fetchObjectives()
    } catch {
      toast({
        title: 'Erro ao excluir objetivo',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    } finally {
      closeDeleteDialog()
    }
  }

  return (
    <Box>
      <Flex flex="1" justifyContent="flex-end" alignItems="center" mb={4}>
        <FormLabel htmlFor="search" fontSize="sm" fontWeight="normal" p={0} m={0} mr={2}>
          Pesquisar
        </FormLabel>
        <Input
          type="search"
          size="sm"
          variant="outline"
          id="search"
          w="200px"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Flex>

      {error ? (
        <Center py={10}>
          <Text color="red.500">Erro ao carregar objetivos de curso</Text>
        </Center>
      ) : (
        <TableContainer>
          <Table>
            <Thead>
              <Tr>
                <Th>ID</Th>
                <Th>Nome</Th>
                <Th>Criado em</Th>
                <Th>Ações</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filtered.length === 0 ? (
                <Tr>
                  <Td colSpan={4} textAlign="center">
                    Nenhum resultado encontrado
                  </Td>
                </Tr>
              ) : (
                filtered.map((objective) => (
                  <Tr key={objective.id}>
                    <Td>{objective.id}</Td>
                    <Td>{objective.name}</Td>
                    <Td>{formatDateTime(objective.created_at)}</Td>
                    <Td>
                      <Tooltip label="Editar">
                        <IconButton
                          aria-label="Editar"
                          icon={<i className="lni lni-pencil" />}
                          colorScheme="blue"
                          size="sm"
                          mr={2}
                          onClick={() => openEditModal({ id: objective.id, name: objective.name })}
                        />
                      </Tooltip>
                      <Tooltip label="Excluir">
                        <IconButton
                          aria-label="Excluir"
                          icon={<i className="lni lni-trash-can" />}
                          colorScheme="red"
                          size="sm"
                          onClick={() => openDeleteDialog(objective.id)}
                        />
                      </Tooltip>
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </TableContainer>
      )}

      <Tooltip label="Adicionar objetivo de curso" aria-label="Adicionar objetivo de curso" placement="left">
        <Button
          colorScheme="magenta"
          position="fixed"
          bottom="20px"
          right="20px"
          borderRadius="full"
          width="70px"
          height="70px"
          onClick={openCreateModal}
        >
          <i className="lni lni-plus"></i>
        </Button>
      </Tooltip>

      <CoursesObjectivesModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onFinished={fetchObjectives}
        initialData={editingObjective}
        service={service}
      />

      <AlertDialog isOpen={isDeleteOpen} leastDestructiveRef={cancelRef} onClose={closeDeleteDialog} isCentered>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Confirmar exclusão
            </AlertDialogHeader>

            <AlertDialogBody>Tem certeza que deseja excluir esse objetivo?</AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={closeDeleteDialog}>
                Cancelar
              </Button>
              <Button colorScheme="red" onClick={handleDelete} ml={3}>
                Excluir
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  )
}

export default CoursesObjectivesPage
